import { useState } from 'react';
import { mealsApi, productsApi, type CorrectionInput } from '@/shared/api/endpoints';
import type { Meal, MealItem, MeasurementUnit, ProductMatch } from '@/shared/api/types';
import { formatGrams, unitLabel } from '@/shared/format/nutrition';
import { describeError } from '@/shared/hooks/useAsync';
import { Banner, Button, Field, Segmented, SelectField, Sheet, Spinner } from '@/shared/ui';
import { OwnProductSheet } from '../products/OwnProductSheet';

const UNITS: MeasurementUnit[] = ['Gram', 'Milliliter', 'Unit', 'Serving'];

/**
 * RF-20 · las tres formas de corregir que el requerimiento nombra: producto, cantidad o macros.
 * Se eligen, no se mezclan, porque cada una responde a una pregunta distinta —"no era eso", "no
 * era tanto", "los números son estos"— y un formulario con las tres abiertas obliga a leerlas
 * todas para contestar una.
 */
export type CorrectionMode = 'quantity' | 'product' | 'macros';

type Mode = CorrectionMode;

const MODES = [
  { value: 'quantity' as const, label: 'Cantidad' },
  { value: 'product' as const, label: 'Producto' },
  { value: 'macros' as const, label: 'Macros' },
];

/**
 * CU-06 · corregir. La corrección gana sobre cualquier estimación posterior y queda registrada
 * como una estimación más, con origen propio: no se pisa nada, se agrega (RN-02).
 */
export function CorrectItemSheet({
  mealId,
  item,
  initialMode = 'quantity',
  onDone,
  onClose,
}: {
  mealId: string;
  item: MealItem;
  /** El detalle del ítem abre la hoja ya parada en la corrección que la persona eligió. */
  initialMode?: Mode;
  onDone: (meal: Meal) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<Mode>(initialMode);

  const [quantity, setQuantity] = useState(item.quantity?.toString() ?? '');
  const [unit, setUnit] = useState<MeasurementUnit | ''>(item.unit ?? '');

  const [query, setQuery] = useState(item.freeText ?? '');
  const [matches, setMatches] = useState<ProductMatch[] | null>(null);
  const [semanticApplied, setSemanticApplied] = useState(true);
  const [productId, setProductId] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [creatingProduct, setCreatingProduct] = useState(false);

  const estimate = item.currentEstimate;
  const [proteinG, setProteinG] = useState(estimate?.macros.proteinG.toString() ?? '');
  const [carbohydratesG, setCarbohydratesG] = useState(
    estimate?.macros.carbohydratesG.toString() ?? '',
  );
  const [fatG, setFatG] = useState(estimate?.macros.fatG.toString() ?? '');
  const [fiberG, setFiberG] = useState(estimate?.macros.fiberG?.toString() ?? '');
  const [sodiumMg, setSodiumMg] = useState(estimate?.macros.sodiumMg?.toString() ?? '');

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const search = async () => {
    setSearching(true);
    setError(null);
    try {
      const result = await productsApi.search(query);
      setMatches(result.matches);
      setSemanticApplied(result.semanticApplied);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setSearching(false);
    }
  };

  const macrosComplete = proteinG !== '' && carbohydratesG !== '' && fatG !== '';

  const ready =
    mode === 'quantity'
      ? quantity !== ''
      : mode === 'product'
        ? productId !== null
        : macrosComplete;

  const submit = async () => {
    setSubmitting(true);
    setError(null);

    // Sólo viaja lo que la persona eligió corregir. Mandar los tres campos siempre haría que
    // "corregí la cantidad" también reafirmara unos macros que nadie miró.
    const correction: CorrectionInput =
      mode === 'quantity'
        ? { quantity: Number(quantity), unit: unit === '' ? null : unit }
        : mode === 'product'
          ? {
              productId,
              quantity: quantity === '' ? null : Number(quantity),
              unit: unit === '' ? null : unit,
            }
          : {
              macros: {
                proteinG: Number(proteinG),
                carbohydratesG: Number(carbohydratesG),
                fatG: Number(fatG),
                fiberG: fiberG === '' ? null : Number(fiberG),
                sodiumMg: sodiumMg === '' ? null : Number(sodiumMg),

                /*
                 * El alcohol se CONSERVA, no se pisa.
                 *
                 * Este formulario edita los cinco valores que muestra, y el alcohol no es uno: no
                 * está en ningún rótulo argentino y pedirlo sería ruido. Pero mandarlo en null
                 * borraría el que ya tenía la estimación, y en una copa de vino eso significa que
                 * corregir la cantidad le hunde las calorías a una fracción — el mismo error que
                 * RN-04 acaba de arreglar, reintroducido desde la pantalla.
                 */
                alcoholG: estimate?.macros.alcoholG ?? null,
              },
            };

    try {
      onDone(await mealsApi.correct(mealId, item.id, correction));
      onClose();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet title="Corregir el ítem" onClose={onClose}>
      {error !== null && <Banner tone="danger">{error}</Banner>}

      <Segmented options={MODES} value={mode} onChange={setMode} label="Qué querés corregir" />

      {(mode === 'quantity' || mode === 'product') && (
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <Field
              label="Cantidad"
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <SelectField
              label="Unidad"
              value={unit}
              onChange={(event) => setUnit(event.target.value as MeasurementUnit | '')}
            >
              <option value="">Sin declarar</option>
              {UNITS.map((option) => (
                <option key={option} value={option}>
                  {unitLabel(option)}
                </option>
              ))}
            </SelectField>
          </div>
        </div>
      )}

      {mode === 'quantity' && (
        <p className="micro muted">
          Los macros se reescalan sobre la estimación vigente en la misma proporción.
        </p>
      )}

      {mode === 'product' && (
        <>
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <Field
                label="Buscar en el catálogo"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                hint="Elegí el producto exacto y la estimación pasa a ser del catálogo."
              />
            </div>
            <Button
              variant="quiet"
              onClick={() => void search()}
              disabled={searching || query.trim() === ''}
            >
              Buscar
            </Button>
          </div>

          {searching && <Spinner label="Buscando" />}

          {matches !== null && !semanticApplied && (
            // Sin proveedor de embeddings el puntaje es sólo difuso (RN-20). Decirlo evita que
            // "no aparece" se lea como "no existe".
            <p className="micro muted">
              La búsqueda por significado no está disponible: estos resultados son por parecido de
              nombre.
            </p>
          )}

          {matches !== null && matches.length === 0 && (
            <>
              <p className="small muted">No hay productos parecidos en el catálogo.</p>
              <Button variant="quiet" block onClick={() => setCreatingProduct(true)}>
                Cargarlo vos
              </Button>
            </>
          )}

          {matches !== null && matches.length > 0 && (
            <ul className="list-reset">
              {matches.map((match) => (
                <li key={match.product.id}>
                  <button
                    type="button"
                    className={
                      productId === match.product.id
                        ? 'btn btn-primary btn-block'
                        : 'btn btn-quiet btn-block'
                    }
                    onClick={() =>
                      setProductId(productId === match.product.id ? null : match.product.id)
                    }
                    style={{ justifyContent: 'space-between', textAlign: 'left' }}
                  >
                    <span>
                      {match.product.name}
                      {match.product.brand !== null && (
                        <span className="micro muted"> · {match.product.brand}</span>
                      )}
                    </span>
                    <span className="micro numeric">
                      {formatGrams(match.product.macros100g.proteinG)} g P
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {mode === 'macros' && (
        <>
          <p className="micro muted">
            Estos son los macros del ítem entero, no por 100 g. Las calorías se calculan con Atwater
            a partir de ellos.
          </p>
          <Field
            label="Proteína (g)"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            required
            value={proteinG}
            onChange={(event) => setProteinG(event.target.value)}
          />
          <Field
            label="Carbohidratos (g)"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            required
            value={carbohydratesG}
            onChange={(event) => setCarbohydratesG(event.target.value)}
          />
          <Field
            label="Grasa (g)"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            required
            value={fatG}
            onChange={(event) => setFatG(event.target.value)}
          />
          <Field
            label="Fibra (g)"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={fiberG}
            onChange={(event) => setFiberG(event.target.value)}
            hint="Opcional."
          />
          <Field
            label="Sodio (mg)"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={sodiumMg}
            onChange={(event) => setSodiumMg(event.target.value)}
            hint="Opcional."
          />
        </>
      )}

      <Button variant="primary" block onClick={() => void submit()} disabled={submitting || !ready}>
        {submitting ? 'Guardando…' : 'Guardar la corrección'}
      </Button>

      {creatingProduct && (
        <OwnProductSheet
          initialName={query}
          onCreated={(product) => {
            // Recién cargado, queda elegido: nadie carga un producto para después no usarlo.
            setMatches([{ product, score: 1, fuzzyScore: 1, semanticScore: null }]);
            setProductId(product.id);
          }}
          onClose={() => setCreatingProduct(false)}
        />
      )}
    </Sheet>
  );
}
